import { useBookingModuleContext } from "PFApp/booking/parts/providers/booking_module_context_provider";
import { CalendarMode, CalendarView } from "PFApp/booking/types";
import { DropdownOption } from "PFComponents/dropdown/dropdown";
import Select from "PFComponents/select/select";
import { Typography } from "PFComponents/typography";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useBookingsOverviewContext } from "./overview/context/bookings_overview_context";
import css from "./toolbar.module.scss";

const CALENDAR_VIEWS_BY_MODE = {
  [CalendarMode.Workforce]: [
    CalendarView.Compressed,
    CalendarView.Utilization,
    CalendarView.Availability,
    CalendarView.BookingsOnly
  ],
  [CalendarMode.Projects]: [CalendarView.EngagementsAndAuditEngagements, CalendarView.RolesAndAuditRoles]
};

interface DisplayElementProps {
  value: string;
  additionalInfo: string;
}

const DisplayElement = ({ value, additionalInfo }: DisplayElementProps) => (
  <>
    <Typography variant="bodyRegular" className={css.viewSelectorValue} noMargin>
      {value}
    </Typography>
    <Typography variant="bodyRegular" className={css.viewSelectorAdditionalInfo} noMargin>
      {additionalInfo}
    </Typography>
  </>
);

export const CalendarViewSelector = () => {
  const { t } = useTranslation("bookingModule");
  const {
    calendarMode,
    showUtilizationMetrics,
    calendarData: { view, setView }
  } = useBookingsOverviewContext();
  const { headerDisabled } = useBookingModuleContext();

  const displayElements = useMemo(
    () => ({
      [CalendarView.EngagementsAndAuditEngagements]: (
        <DisplayElement
          value={t(`viewMode.engagementsAndAuditEngagements`)}
          additionalInfo={t(`viewMode.engagementsAndAuditEngagementsAdditionalInfo`)}
        />
      ),
      [CalendarView.RolesAndAuditRoles]: (
        <DisplayElement
          value={t(`viewMode.rolesAndAuditRoles`)}
          additionalInfo={t(`viewMode.rolesAndAuditRolesAdditionalInfo`)}
        />
      )
    }),
    [t]
  );

  const calendarViewOptions: DropdownOption[] = useMemo(
    () =>
      Object.values(CalendarView)
        .filter((viewMode) => CALENDAR_VIEWS_BY_MODE[calendarMode]?.includes(viewMode))
        .map((viewMode) => ({
          id: viewMode,
          item: viewMode,
          displayElement: displayElements[viewMode] ?? t(`viewMode.${viewMode}`)
        }))
        .filter(
          ({ id }) =>
            calendarMode !== CalendarMode.Workforce ||
            showUtilizationMetrics ||
            id !== CalendarView.Utilization
        ),
    [showUtilizationMetrics, t, calendarMode, displayElements]
  );

  return (
    <Select
      // @ts-ignore
      readOnly
      disabled={headerDisabled}
      kind="bordered"
      value={t(`viewMode.${view}`)}
      controlledValue
      rootClassName={css.toolbarSelect}
      handleChange={setView}
      selectedIndex={calendarViewOptions.findIndex(({ id }) => id === view)}
      options={calendarViewOptions}
    />
  );
};
